import { olderGeneration, youngerGeneration } from '@/assets/messages';
import { PrimaryAnnotatedType } from '@/components/annotated-type';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { Scatterplot } from '@/components/scatterplot';
import { countryIdToName } from '@/utils/country-id-to-name';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useState } from 'react';

type ChildhoodStridesChapter2Props = {
  data: ChildhoodStridesData['strides_country_generation_averages'];
};

export function ChildhoodStridesChapter2(props: ChildhoodStridesChapter2Props) {
  const { data } = props;

  const { i18n } = useLingui();

  const [highlightedIds, setHighlightedIds] = useState<CountryIsoCode[]>();

  const axisLabel = (generation: string) =>
    t({
      id: 'childhood-strides.scatterplot.axis-label',
      message: 'More {generation} say on average childhood is better today',
      values: { generation },
    });

  const scatterplotData = (Object.entries(data) as [CountryIsoCode, AgeGroupBreakdown][]).map(
    ([key, value]) => ({
      id: key,
      label: countryIdToName(key),
      ...value,
    })
  );

  const handleStepEnter = async (evt: OnStepEnterExitArgs<CountryIsoCode[]>) => {
    setHighlightedIds(evt.data);
  };

  return (
    <ScrollRoot>
      <ScrollGraphic>
        <Scatterplot
          data={scatterplotData}
          highlightedIds={highlightedIds}
          idsAlignment={{ MA: 'bottom', PE: 'top', UA: 'bottom', IN: 'top' }} // overriding alignment for specific country labels
          getXValue={(d) => d.older}
          getYValue={(d) => d.younger}
          topAxisLabel={axisLabel(youngerGeneration(i18n))}
          bottomAxisLabel={axisLabel(olderGeneration(i18n))}
          hasLabels
        />
      </ScrollGraphic>

      <ScrollSteps onStepEnter={handleStepEnter}>
        <ScrollStep>
          <StoryText>
            <Trans id="childhood-strides.chapter-2.text-1">
              Overall, young people are more likely to acknowledge improvements in children’s lives.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep data={['UA', 'PE']}>
          <StoryText>
            <Trans id="childhood-strides.chapter-2.text-2">
              That’s true in nearly all countries, but especially so in{' '}
              <PrimaryAnnotatedType>{countryIdToName('UA')}</PrimaryAnnotatedType> and{' '}
              <PrimaryAnnotatedType>{countryIdToName('PE')}.</PrimaryAnnotatedType>
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep data={['MA', 'IN', 'BD']}>
          <StoryText>
            <Trans id="childhood-strides.chapter-2.text-3">
              By contrast, young people are no more likely than older people to believe children's
              lives have improved in{' '}
              <PrimaryAnnotatedType>{countryIdToName('MA')},</PrimaryAnnotatedType>{' '}
              <PrimaryAnnotatedType>{countryIdToName('IN')},</PrimaryAnnotatedType> and{' '}
              <PrimaryAnnotatedType>{countryIdToName('BD')}.</PrimaryAnnotatedType>
            </Trans>
          </StoryText>
        </ScrollStep>
      </ScrollSteps>
    </ScrollRoot>
  );
}
